import { useState, useEffect, useRef } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { userCanAccessProject } from '../lib/projectAuth';
import { ProjectData } from '../types';
import { environment } from '../config/environment';
import { useNotificationContext } from '../contexts/NotificationContext';
import {
  formatSubmissionErrorForUser,
  type SubmissionErrorResponse,
} from '../utils/submissionErrors';

export function useProject(jiraKey: string) {
  const [project, setProject] = useState<ProjectData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authorized, setAuthorized] = useState<boolean | null>(null);
  const { showSuccess, showError, showInfo } = useNotificationContext();
  const previousStatusRef = useRef<string | null>(null);

  // Get status change notification message
  const getStatusChangeMessage = (newStatus: string, appType: 'versirent' | 'idealone'): { title: string; message: string; type: 'success' | 'info' | 'warning' } => {
    const appName = appType === 'versirent' ? 'VersiRent' : 'IdealOne';
    
    switch (newStatus) {
      case 'stage2_provisioning':
        return {
          title: 'Project Submitted',
          message: `Your ${appName} project has been submitted and is now being provisioned. We'll keep you updated on progress.`,
          type: 'success'
        };
      case 'stage3_complete':
        return {
          title: 'Setup Complete',
          message: `Your ${appName} project is complete! Check your portal for details.`,
          type: 'success'
        };
      case 'stage4_deployed':
        return {
          title: 'App Live',
          message: `Your ${appName} app has passed App Store review and is live / published!`,
          type: 'success'
        };
      case 'customer_review':
        return {
          title: 'Review Required',
          message: `Your ${appName} application is ready for App Store submission! Please review and approve your app configuration.`,
          type: 'info'
        };
      case 'resolve_issues':
        return {
          title: 'Change Request Received',
          message: `We received your feedback on ${appName}. Our team is reviewing your change request.`,
          type: 'info'
        };
      case 'apple_agreement_update_needed':
        return {
          title: 'Action Required',
          message: `Apple has updated their Developer Program License Agreement. You must accept this update to continue with your ${appName} project.`,
          type: 'warning'
        };
      case 'submit_to_app_store':
        return {
          title: 'App Store Submission',
          message: `Great news! We are now preparing and submitting your ${appName} app to the App Store.`,
          type: 'info'
        };
      case 'waiting_on_app_store_approval':
        return {
          title: 'Under Review',
          message: `Your ${appName} app has been submitted and is now under review by Apple and/or Google.`,
          type: 'info'
        };
      case 'deployment_finished':
        return {
          title: '🎉 App Live!',
          message: `Congratulations! Your ${appName} app is now live and available for download on the App Stores!`,
          type: 'success'
        };
      case 'stage_for_update':
        return {
          title: 'Update Available',
          message: `A new version of your ${appName} app is ready. We're transitioning you to the latest version.`,
          type: 'info'
        };
      default:
        return {
          title: 'Status Updated',
          message: `Your ${appName} project status has been updated to: ${newStatus}.`,
          type: 'info'
        };
    }
  };

  // Project access is enforced by Firebase Auth custom claims + Firestore rules.

  useEffect(() => {
    if (!jiraKey) {
      setLoading(false);
      return;
    }

    let unsubscribeSnapshot: (() => void) | undefined;

    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      if (unsubscribeSnapshot) {
        unsubscribeSnapshot();
        unsubscribeSnapshot = undefined;
      }

      if (!firebaseUser) {
        setError('No active session found');
        setAuthorized(false);
        setLoading(false);
        return;
      }

      const allowed = await userCanAccessProject(jiraKey, firebaseUser);
      if (!allowed) {
        setError('You do not have access to this project');
        setAuthorized(false);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      setAuthorized(true);

      unsubscribeSnapshot = onSnapshot(
        doc(db, 'projects', jiraKey),
        (projectDoc) => {
          if (projectDoc.exists()) {
            const newProject = projectDoc.data() as ProjectData;

            if (previousStatusRef.current && previousStatusRef.current !== newProject.status) {
              console.log(`🔄 Status changed from ${previousStatusRef.current} to ${newProject.status}`);
              const statusMessage = getStatusChangeMessage(newProject.status, newProject.appType);

              if (statusMessage.type === 'success') {
                showSuccess(statusMessage.title, statusMessage.message);
              } else if (statusMessage.type === 'warning') {
                showError(statusMessage.title, statusMessage.message);
              } else {
                showInfo(statusMessage.title, statusMessage.message);
              }
            }

            if (previousStatusRef.current === null) {
              previousStatusRef.current = newProject.status;
            } else {
              previousStatusRef.current = newProject.status;
            }
            setProject(newProject);
          } else {
            setError('Project not found');
          }
          setLoading(false);
        },
        (snapshotError) => {
          console.error('Error fetching project:', snapshotError);
          setError('Failed to load project');
          setLoading(false);
        },
      );
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeSnapshot) {
        unsubscribeSnapshot();
      }
    };
  }, [jiraKey, showError, showInfo, showSuccess]);

  const updateFormData = async (formData: Partial<ProjectData['formData']>) => {
    if (!jiraKey) return;

    try {
      // Get user email from session for authorization
      const userSession = localStorage.getItem('userSession');
      const userEmail = userSession ? JSON.parse(userSession).email : null;

      // Use Cloud Function for secure updates instead of direct Firestore writes
      const functionsUrl = environment.useEmulators 
        ? `http://127.0.0.1:5001/ima-onboard/us-central1/updateFormData`
        : `https://us-central1-ima-onboard.cloudfunctions.net/updateFormData`;
      
      const response = await fetch(functionsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          jiraKey, 
          formData: formData, // Only send the specific fields being updated
          userEmail
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update form data');
      }

      // Footer (Last saved / Save failed) is the status surface for auto-save.
      // Do not toast here — showError defaults to persistent and leaves a stale
      // "Save Failed" after transient network blips even when later saves succeed.
    } catch (error) {
      console.error('Error updating form data:', error);
      throw error;
    }
  };

  const updateStatus = async (status: ProjectData['status']) => {
    if (!jiraKey) return;

    try {
      // Get user email from session for authorization
      const userSession = localStorage.getItem('userSession');
      const userEmail = userSession ? JSON.parse(userSession).email : null;

      // Use Cloud Function for secure updates instead of direct Firestore writes
      const functionsUrl = environment.useEmulators 
        ? `http://127.0.0.1:5001/ima-onboard/us-central1/updateProjectStatus`
        : `https://us-central1-ima-onboard.cloudfunctions.net/updateProjectStatus`;
      
      const response = await fetch(functionsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ jiraKey, status, userEmail }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update project status');
      }

      showSuccess('Status Updated', 'Project status has been updated successfully');
    } catch (error) {
      console.error('Error updating project status:', error);
      showError('Status Update Failed', 'Failed to update project status. Please try again.');
      throw error;
    }
  };

  const submitForm = async (
    formData: ProjectData['formData'] & { notifications?: ProjectData['notifications'] }
  ) => {
    if (!jiraKey) return;

    try {
      const { notifications: submissionNotifications, ...formFields } = formData;

      // Get user email from session for authorization
      const userSession = localStorage.getItem('userSession');
      const userEmail = userSession ? JSON.parse(userSession).email : null;

      console.log('🚀 Frontend: Starting form submission:', { jiraKey, formDataKeys: Object.keys(formFields), userEmail });
      console.log('📋 Frontend: Form data sample:', {
        appName: formFields.appName,
        companyDescription: formFields.companyDescription,
        appStoreContactEmail: formFields.appStoreContactEmail,
        appStoreContactPhone: formFields.appStoreContactPhone,
        ecommerceType: formFields.ecommerceType,
        redirectWebsiteUrl: formFields.redirectWebsiteUrl
      });

      // Use the new submitFormData Cloud Function that handles both Firebase and Jira updates
      const functionsUrl = environment.useEmulators 
        ? `http://127.0.0.1:5001/ima-onboard/us-central1/submitFormData`
        : `https://us-central1-ima-onboard.cloudfunctions.net/submitFormData`;
      
      console.log('📡 Frontend: Calling Cloud Function:', functionsUrl);
      
      // Transform formData to match backend expectations
      // Convert testerEmails from comma-separated string to array
      const transformedFormData = { ...formFields };
      const testerEmailsValue = formFields.testerEmails as any; // Form stores as string, but type expects array
      if (typeof testerEmailsValue === 'string' && testerEmailsValue.trim()) {
        transformedFormData.testerEmails = testerEmailsValue
          .split(',')
          .map((email: string) => email.trim())
          .filter((email: string) => email.length > 0);
      } else if (Array.isArray(testerEmailsValue) && testerEmailsValue.length > 0) {
        // Already an array, keep as-is
        transformedFormData.testerEmails = testerEmailsValue;
      } else {
        transformedFormData.testerEmails = null;
      }
      
      const requestBody: {
        jiraKey: string;
        formData: typeof transformedFormData;
        userEmail: string | null;
        notifications?: ProjectData['notifications'];
      } = { jiraKey, formData: transformedFormData, userEmail };
      if (submissionNotifications && Array.isArray(submissionNotifications)) {
        requestBody.notifications = submissionNotifications;
      }
      console.log('📦 Frontend: Request payload:', requestBody);
      
      const response = await fetch(functionsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      console.log('📡 Frontend: Response status:', response.status);
      console.log('📡 Frontend: Response headers:', Object.fromEntries(response.headers.entries()));

      const responseData = (await response.json()) as SubmissionErrorResponse;
      console.log('📡 Frontend: Response data:', responseData);

      if (!response.ok) {
        const { title, message } = formatSubmissionErrorForUser(responseData);
        showError(title, message);
        const submissionError = new Error(
          responseData.errorCode || responseData.message || 'Failed to submit form data'
        ) as Error & { submissionErrorShown?: boolean };
        submissionError.submissionErrorShown = true;
        throw submissionError;
      }

      showSuccess('Form Submitted', 'Your application has been submitted successfully and synced to Jira');
    } catch (error) {
      console.error('❌ Frontend: Error submitting form:', error);
      const shown = (error as Error & { submissionErrorShown?: boolean })?.submissionErrorShown;
      if (!shown) {
        const { title, message } = formatSubmissionErrorForUser(null);
        showError(title, message);
      }
      throw error;
    }
  };

  return {
    project,
    loading,
    error,
    authorized,
    updateFormData,
    updateStatus,
    submitForm,
  };
}
